import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API, STAGE_META, STAGE_OPENER, LAST_STEP } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Layout from "@/components/Layout";
import {
  ArrowLeft, ArrowRight, Share2, Download, CheckCircle2, Sparkles, Copy, Loader2,
  LifeBuoy, FileText, FileDown, ClipboardList, Mic, MicOff, MessageCircleHeart,
} from "lucide-react";
import { toast } from "sonner";
import ConflictPlanDialog from "@/components/ConflictPlanDialog";
import FeedbackDialog from "@/components/FeedbackDialog";

const STUCK_STEPS = new Set([2, 3, 4]); // Your needs, Their needs, Strategies
const STUCK_MESSAGE = "I'm not sure — could you suggest some options?";

function BotBubble({ text }) {
  return (
    <div className="flex gap-3 msg-appear" data-testid="coach-bubble">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
        <Sparkles className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
      </div>
      <div className="bg-muted text-foreground rounded-2xl rounded-tl-sm p-5 max-w-[85%] leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

function UserBubble({ text }) {
  return (
    <div className="flex justify-end msg-appear" data-testid="user-bubble">
      <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm p-5 max-w-[85%] leading-relaxed whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex gap-3" data-testid="typing-indicator">
      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-1">
        <Sparkles className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
      </div>
      <div className="bg-muted rounded-2xl rounded-tl-sm px-5 py-4">
        <span className="typing-dot"></span>
        <span className="typing-dot"></span>
        <span className="typing-dot"></span>
      </div>
    </div>
  );
}

export default function Session() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sessionDoc, setSessionDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [planOpen, setPlanOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const recognitionRef = useRef(null);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/sessions/${id}`);
      setSessionDoc(r.data);
    } catch {
      toast.error("Could not load session");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [sessionDoc, sending]);

  if (loading || !sessionDoc) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-16 text-muted-foreground" data-testid="session-loading">
          Loading your session…
        </div>
      </Layout>
    );
  }

  const stage = sessionDoc.current_stage;
  const stageInfo = STAGE_META[stage];
  const stageData = sessionDoc.stages?.[String(stage)] || { messages: [], summary: "" };
  const messages = stageData.messages || [];
  const progress = (stage / LAST_STEP) * 100;
  const isFinal = !!sessionDoc.final_summary;

  const sendMessage = async (overrideText) => {
    const text = (overrideText !== undefined ? overrideText : input).trim();
    if (!text || sending) return;
    setSending(true);
    // optimistic user msg
    const optimistic = { role: "user", content: text, timestamp: new Date().toISOString() };
    setSessionDoc((d) => ({
      ...d,
      stages: {
        ...d.stages,
        [String(stage)]: {
          ...d.stages[String(stage)],
          messages: [...(d.stages[String(stage)]?.messages || []), optimistic],
        },
      },
    }));
    if (overrideText === undefined) setInput("");
    try {
      const r = await api.post(`/sessions/${id}/message`, { content: text });
      const advancedTo = r.data.advanced_to;
      setSessionDoc((d) => {
        const prev = d.stages[String(stage)]?.messages || [];
        // remove optimistic user (last one) and append server pair
        const withoutOpt = prev.slice(0, -1);
        const nextStages = {
          ...d.stages,
          [String(stage)]: {
            ...d.stages[String(stage)],
            messages: [...withoutOpt, r.data.user_message, r.data.assistant_message],
          },
        };
        const next = { ...d, stages: nextStages };
        if (advancedTo !== undefined && advancedTo !== null) {
          next.current_stage = advancedTo;
        }
        return next;
      });
      if (advancedTo !== undefined && advancedTo !== null) {
        // Refetch full doc so any server-side computed pieces (summaries, step-7 auto-draft) are in sync
        try {
          const fresh = await api.get(`/sessions/${id}`);
          setSessionDoc(fresh.data);
        } catch {
          // non-fatal — local state already updated
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coach is unavailable right now.");
      // rollback
      setSessionDoc((d) => {
        const prev = d.stages[String(stage)]?.messages || [];
        return {
          ...d,
          stages: {
            ...d.stages,
            [String(stage)]: {
              ...d.stages[String(stage)],
              messages: prev.slice(0, -1),
            },
          },
        };
      });
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const askForSuggestions = () => sendMessage(STUCK_MESSAGE);

  const toggleVoice = () => {
    if (recording) {
      try { recognitionRef.current?.stop(); } catch {}
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser. Try Chrome, Edge, or Safari.");
      return;
    }
    const rec = new SR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US";
    // Snapshot whatever the user has already typed so we can append cleanly.
    let baseInput = "";
    setInput((prev) => { baseInput = prev || ""; return prev; });
    let finalText = "";
    const join = (base, finalT, interimT) => {
      const sep = base && !base.endsWith(" ") ? " " : "";
      return base + sep + (finalT + interimT).replace(/\s+/g, " ");
    };
    rec.onstart = () => setRecording(true);
    rec.onend = () => {
      setRecording(false);
      // Commit final text only.
      setInput(join(baseInput, finalText.trim(), "").trimEnd());
    };
    rec.onerror = (e) => {
      setRecording(false);
      const msg = e.error === "not-allowed" ? "Microphone permission denied." : `Voice error: ${e.error}`;
      toast.error(msg);
    };
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interim += r[0].transcript;
      }
      // Live preview: base + finals so far + current interim
      setInput(join(baseInput, finalText, interim));
    };
    recognitionRef.current = rec;
    try { rec.start(); } catch (err) { toast.error("Could not start voice input."); }
  };

  const goTo = async (target) => {
    if (target < 0 || target > LAST_STEP || advancing) return;
    setAdvancing(true);
    try {
      const r = await api.post(`/sessions/${id}/advance`, { stage: target });
      setSessionDoc(r.data);
    } catch {
      toast.error("Could not advance");
    } finally {
      setAdvancing(false);
    }
  };

  const finishSession = async () => {
    setCompleting(true);
    try {
      const r = await api.post(`/sessions/${id}/complete`);
      setSessionDoc(r.data);
      toast.success("Your summary is ready.");
    } catch {
      toast.error("Could not finalize the session");
    } finally {
      setCompleting(false);
    }
  };

  const openShare = async () => {
    try {
      const r = await api.post(`/sessions/${id}/share`);
      const url = `${window.location.origin}/shared/${r.data.share_token}`;
      setShareUrl(url);
      setShareOpen(true);
    } catch {
      toast.error("Could not create share link");
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  const exportMarkdown = async () => {
    try {
      const r = await api.get(`/sessions/${id}/export`);
      const blob = new Blob([r.data.markdown], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.data.filename || "session.md";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const exportPdf = async () => {
    try {
      const token = localStorage.getItem("crc_token");
      const res = await fetch(`${API}/sessions/${id}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeTitle = (sessionDoc?.title || "session").replace(/\s+/g, "_");
      a.download = `${safeTitle}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF export failed");
    }
  };

  const showOpener = messages.length === 0 && STAGE_OPENER[stage];
  const prevStageSummary = stage > 0
    ? (sessionDoc.stages?.[String(stage - 1)]?.summary || "")
    : "";
  const prevStageMeta = stage > 0 ? STAGE_META[stage - 1] : null;

  return (
    <Layout>
      {/* Sticky progress header — fully opaque */}
      <div className="sticky top-[65px] z-20 bg-background border-b border-border/60 shadow-sm">
        <div className="max-w-3xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Step {stage + 1} of {LAST_STEP + 1}
              </p>
              <h2 className="text-lg sm:text-xl font-medium truncate" style={{ fontFamily: 'Manrope' }} data-testid="session-stage-title">
                {stageInfo.title}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={openShare} className="rounded-full" data-testid="share-button">
                <Share2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Share
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-full" data-testid="export-button">
                    <Download className="w-4 h-4 mr-1" strokeWidth={1.5} /> Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-xl">
                  <DropdownMenuItem onClick={exportMarkdown} data-testid="export-markdown-option" className="cursor-pointer">
                    <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> Markdown (.md)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={exportPdf} data-testid="export-pdf-option" className="cursor-pointer">
                    <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} /> PDF (.pdf)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <Progress value={progress} className="h-2 bg-secondary" data-testid="progress-bar" />
          {/* Single-line 5-label strip, aligned with bar positions */}
          <div className="hidden sm:flex justify-between mt-3 text-[10px] uppercase tracking-widest gap-2">
            {STAGE_META.map((s) => {
              const active = s.stage === stage;
              const reached = s.stage <= stage;
              return (
                <span
                  key={s.stage}
                  className={`whitespace-nowrap ${
                    active ? "text-primary font-medium" : reached ? "text-foreground/60" : "text-muted-foreground"
                  }`}
                  data-testid={`stage-label-${s.stage}`}
                >
                  {s.short}
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 fade-in">
        {/* Final summary view */}
        {isFinal ? (
          <FinalSummaryView
            doc={sessionDoc}
            onBack={() => navigate("/dashboard")}
            onExportMd={exportMarkdown}
            onExportPdf={exportPdf}
            onShare={openShare}
            onOpenPlan={() => setPlanOpen(true)}
            onOpenFeedback={() => setFeedbackOpen(true)}
          />
        ) : (
          <>
            <div className="space-y-5" data-testid="chat-area">
              {prevStageSummary && prevStageMeta && messages.length === 0 && (
                <div
                  className="flex items-start gap-3 px-4 py-3 rounded-2xl bg-secondary/50 border border-border/60"
                  data-testid="prev-step-breadcrumb"
                >
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <ArrowLeft className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
                  </div>
                  <div className="text-sm text-foreground/75 leading-relaxed">
                    <span className="text-xs uppercase tracking-widest text-muted-foreground">
                      Just covered — Step {stage - 1}: {prevStageMeta.title}
                    </span>
                    <p className="mt-1">{prevStageSummary}</p>
                  </div>
                </div>
              )}
              {showOpener && <BotBubble text={STAGE_OPENER[stage]} />}
              {messages.map((m, i) => (
                m.role === "user" ? <UserBubble key={i} text={m.content} /> : <BotBubble key={i} text={m.content} />
              ))}
              {sending && <TypingBubble />}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <Card className="mt-8 p-4 rounded-2xl border-border/60 shadow-none">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Type your reply, or tap the mic to speak…"
                rows={3}
                className="resize-none border-0 focus-visible:ring-0 p-2 text-base bg-transparent"
                data-testid="chat-input"
                disabled={sending}
                autoFocus
              />
              <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant={recording ? "default" : "outline"}
                    size="sm"
                    onClick={toggleVoice}
                    disabled={sending}
                    className={`rounded-full text-xs ${recording ? "" : "border-dashed"}`}
                    data-testid="voice-input-button"
                    aria-label={recording ? "Stop voice input" : "Start voice input"}
                  >
                    {recording ? <MicOff className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> : <Mic className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />}
                    {recording ? "Listening…" : "Speak"}
                  </Button>
                  {STUCK_STEPS.has(stage) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={askForSuggestions}
                      disabled={sending}
                      className="rounded-full border-dashed text-xs"
                      data-testid="stuck-suggest-button"
                    >
                      <LifeBuoy className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> I'm stuck — suggest options
                    </Button>
                  )}
                </div>
                {sending && (
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
                  </span>
                )}
              </div>
            </Card>

            {/* Nav: Back + Move on (or Finish on the last step) */}
            <div className="mt-6">
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => goTo(stage - 1)}
                  disabled={stage === 0 || advancing}
                  className="rounded-full text-muted-foreground"
                  data-testid="back-stage-button"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} /> Back
                </Button>
                {stage < LAST_STEP ? (
                  <Button
                    onClick={() => goTo(stage + 1)}
                    disabled={advancing}
                    className="rounded-full"
                    data-testid="next-stage-button"
                  >
                    {advancing ? "Saving…" : <>Move on <ArrowRight className="w-4 h-4 ml-1" strokeWidth={1.5} /></>}
                  </Button>
                ) : (
                  <Button
                    onClick={finishSession}
                    disabled={completing}
                    className="rounded-full"
                    data-testid="finish-session-button"
                  >
                    {completing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Finalizing…</> : <><CheckCircle2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Finish &amp; summarize</>}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="rounded-3xl" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Shareable link</DialogTitle>
            <DialogDescription>
              Anyone with this link can read the session. They cannot edit it.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 bg-muted rounded-xl p-3 overflow-hidden">
            <span className="text-sm truncate flex-1" data-testid="share-url">{shareUrl}</span>
            <Button size="sm" onClick={copyShare} className="rounded-full shrink-0" data-testid="copy-share-button">
              <Copy className="w-4 h-4 mr-1" strokeWidth={1.5} /> Copy
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShareOpen(false)} className="rounded-full">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConflictPlanDialog open={planOpen} onOpenChange={setPlanOpen} sessionId={id} />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </Layout>
  );
}

function FinalSummaryView({ doc, onBack, onExportMd, onExportPdf, onShare, onOpenPlan, onOpenFeedback }) {
  return (
    <div className="space-y-8" data-testid="final-summary">
      <Card className="p-8 rounded-3xl border-border/60 shadow-none">
        <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Final summary</p>
        <h1 className="text-3xl tracking-tight mb-6" style={{ fontFamily: 'Manrope' }}>
          {doc.title}
        </h1>
        <div className="prose prose-sm max-w-none text-foreground/85 whitespace-pre-wrap leading-relaxed">
          {doc.final_summary}
        </div>
      </Card>

      <Card className="p-6 sm:p-8 rounded-3xl border border-primary/25 bg-primary/5 shadow-none" data-testid="plan-cta-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <ClipboardList className="w-5 h-5 text-primary" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-medium mb-1" style={{ fontFamily: 'Manrope' }}>
              Your Conflict Plan is ready
            </h3>
            <p className="text-sm text-foreground/75 leading-relaxed mb-4">
              A calm, one-page reflection and preparation document you can print, copy, or carry into the conversation.
            </p>
            <Button
              onClick={onOpenPlan}
              className="rounded-full h-11 px-6"
              data-testid="open-conflict-plan-button"
            >
              <ClipboardList className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Download My Conflict Plan
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-border/60 shadow-none">
        <h3 className="font-medium mb-4" style={{ fontFamily: 'Manrope' }}>Step notes</h3>
        <div className="space-y-4">
          {STAGE_META.map((s) => {
            const sd = doc.stages?.[String(s.stage)];
            if (!sd?.summary) return null;
            return (
              <div key={s.stage} className="border-l-2 border-primary/30 pl-4">
                <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                  Step {s.stage} — {s.title}
                </p>
                <p className="text-sm text-foreground/80 leading-relaxed">{sd.summary}</p>
              </div>
            );
          })}
        </div>
      </Card>

      <div className="flex flex-wrap gap-3 justify-between">
        <Button variant="outline" onClick={onBack} className="rounded-full" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-1" strokeWidth={1.5} /> Back to dashboard
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onShare} className="rounded-full" data-testid="summary-share-button">
            <Share2 className="w-4 h-4 mr-1" strokeWidth={1.5} /> Share
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="summary-export-button">
                <Download className="w-4 h-4 mr-1" strokeWidth={1.5} /> Transcript
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <DropdownMenuItem onClick={onExportMd} data-testid="summary-export-md-option" className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} /> Markdown (.md)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportPdf} data-testid="summary-export-pdf-option" className="cursor-pointer">
                <FileDown className="w-4 h-4 mr-2" strokeWidth={1.5} /> Full transcript PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Big under-development feedback CTA at the end of the flow */}
      <Card
        className="p-6 sm:p-8 rounded-3xl border-2 border-dashed border-primary/40 bg-primary/5 shadow-none text-center"
        data-testid="final-feedback-cta"
      >
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
            <MessageCircleHeart className="w-6 h-6 text-primary" strokeWidth={1.5} />
          </div>
          <h3 className="text-2xl tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Please… PLEASE! We want your feedback.
          </h3>
          <p className="text-sm text-foreground/75 leading-relaxed max-w-xl">
            This app is under active development and every note you send shapes what we build next.
            What was helpful? What felt off? What is missing? Two lines is enough.
          </p>
          <Button
            onClick={onOpenFeedback}
            className="rounded-full h-11 px-6 mt-1"
            data-testid="final-open-feedback-button"
          >
            <MessageCircleHeart className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Help us improve
          </Button>
        </div>
      </Card>
    </div>
  );
}
